import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { callAdmin, AdminApiError } from '../lib/api'
import { demoEnabled, demoIdentity } from '../lib/demo'
import { hasSession, signInWithPassword, signOut } from '../lib/cloudbase'
import type { AdminMe } from '../types'

type AuthStatus = 'loading' | 'guest' | 'authenticated' | 'forbidden'

interface AuthContextValue {
  status: AuthStatus
  me: AdminMe | null
  error: string
  login(username: string, password: string): Promise<void>
  logout(): Promise<void>
  can(capability: string): boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [me, setMe] = useState<AdminMe | null>(null)
  const [error, setError] = useState('')

  const loadIdentity = useCallback(async () => {
    if (demoEnabled) {
      setMe(demoIdentity())
      setStatus('authenticated')
      return
    }
    const active = await hasSession()
    if (!active) {
      setMe(null)
      setStatus('guest')
      return
    }
    try {
      const identity = await callAdmin<AdminMe>('admin.me')
      setMe(identity)
      setStatus('authenticated')
    } catch (caught) {
      if (caught instanceof AdminApiError && caught.code === 'FORBIDDEN') {
        setStatus('forbidden')
        setError(caught.message)
        return
      }
      throw caught
    }
  }, [])

  useEffect(() => {
    loadIdentity().catch(caught => {
      setStatus('guest')
      setError(caught instanceof Error ? caught.message : '无法恢复后台会话')
    })
  }, [loadIdentity])

  const login = useCallback(async (username: string, password: string) => {
    setError('')
    await signInWithPassword(username, password)
    await loadIdentity()
  }, [loadIdentity])

  const logout = useCallback(async () => {
    if (!demoEnabled) await signOut()
    setMe(null)
    setStatus('guest')
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    status,
    me,
    error,
    login,
    logout,
    can: capability => Boolean(me?.capabilities.includes(capability)),
  }), [error, login, logout, me, status])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('AuthProvider 尚未初始化')
  return value
}
